import { useGetDetailedEscrowDataQuery } from 'API/order/escrow/EscrowBaseAPI';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { assertDefined } from 'utilities/Assertions';
import { useMemo } from 'react';
import { IEscrowFileCompleteData } from 'types/order/escrow/IEscrowFileCompleteData';
import { IDisbursementFormData } from 'types/order/escrow/IDisbursementFormData';
import { IDisburseAllDisbursementData } from 'types/order/escrow/IDisburseAllDisbursementData';
import { useGetSectionDataQuery } from 'API/order/fees/FeeApi';
import { sortByCreatedAt } from '../SortByCreatedAt';

type IDisbursementEscrowData = Pick<
  IEscrowFileCompleteData,
  'wireDisbursements' | 'achPushDisbursements' | 'manualCheckDisbursements' | 'restrictedInternalTransfers'
>;

export const useGetAllDisbursements = ({ orderId }: { orderId: string }): IDisburseAllDisbursementData[] => {
  const { data = [] } = useGetSectionDataQuery(orderId);

  const disbursementIds = useMemo(
    () =>
      data.flatMap((section) =>
        section.statementSettlementItems.flatMap((lineItem) =>
          lineItem.statementFees.flatMap((fee) => fee.disbursementIds.map((id) => ({ id, fee, lineItem })))
        )
      ),
    [data]
  );

  const { data: escrowData = {} } = useGetDetailedEscrowDataQuery(orderId);

  const disbursements = useMemo(
    () =>
      disbursementIds
        .map(({ id, fee, lineItem }) => ({
          disbursement: convertEscrowDataToDisbursementData(escrowData)(id),
          fee,
          lineItem,
        }))
        .filter((t) => !!t.disbursement)
        .map((t) => t as IDisburseAllDisbursementData),
    [disbursementIds, escrowData]
  );

  return disbursements;
};

const convertEscrowDataToDisbursementData =
  ({
    wireDisbursements = [],
    manualCheckDisbursements = [],
    achPushDisbursements = [],
    restrictedInternalTransfers = [],
  }: IDisbursementEscrowData) =>
  (id: string) => {
    const wire = wireDisbursements.find((t) => t.id === id);
    if (wire) {
      return {
        ...wire,
        accountNumberConfirmation: wire.accountNumber,
        type: 'Wire',
      };
    }
    const check = manualCheckDisbursements.find((t) => t.id === id);
    if (check) {
      return {
        ...check,
        type: 'Check',
      };
    }

    const ach = achPushDisbursements.find((t) => t.id === id);
    if (ach) {
      return {
        ...ach,
        account: {
          ...ach.account,
          accountNumberConfirmation: ach.account?.accountNumber,
        },
        type: 'ACH',
      };
    }

    const internalTransfer = restrictedInternalTransfers.find((t) => t.id === id);
    if (internalTransfer) {
      const { destinationAccount } = internalTransfer;
      return {
        ...internalTransfer,
        destinationAccountId: destinationAccount?.id,
        type: 'Internal Transfer',
      };
    }
    return false;
  };

export const useGetDisbursementsForSettlementItem = (disbursementIds: string[][] = [[]]): IDisbursementFormData[][] => {
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);
  const { data = {} as IEscrowFileCompleteData } = useGetDetailedEscrowDataQuery(orderId);

  return useMemo(() => {
    return disbursementIds.map((idGroup) => {
      return idGroup
        .map(convertEscrowDataToDisbursementData(data))
        .filter((t) => !!t)
        .map((t) => t as IDisbursementFormData)
        .toSorted(sortByCreatedAt);
    });
  }, [data, disbursementIds]);
};
